import { formatBRL, formatDateBR, monthYearLongBR, formatDateLongBR, driveImageUrl } from './formatters';
import { ESPELHO_THEMES } from './constants';

const ALIQUOTA_MAP = {
  ISS: 'aliquota_iss', PIS: 'aliquota_pis', COFINS: 'aliquota_cofins',
  CSLL: 'aliquota_csll', IR: 'aliquota_ir', INSS: 'aliquota_inss',
};
const fmtPct = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

function calcularRetencoes(solicitacao, servico) {
  const valorBruto = Number(solicitacao.valor) || 0;
  const retidos = (solicitacao.impostos_retidos || []).filter(r => r !== 'Sem retenção');
  const linhas = retidos.map(nome => {
    const col = ALIQUOTA_MAP[nome];
    const raw = col ? servico?.[col] : null;
    const cadastrada = raw !== null && raw !== undefined && raw !== '';
    const aliquota = cadastrada ? Number(raw) : 0;
    const valor = cadastrada ? (aliquota / 100) * valorBruto : 0;
    return { nome, aliquota, cadastrada, valor };
  });
  const totalRetido = linhas.filter(l => l.cadastrada).reduce((a, l) => a + l.valor, 0);
  return { linhas, totalRetido, valorLiquido: valorBruto - totalRetido, valorBruto };
}

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
);

/**
 * HTML de email do espelho da NF — inline styles, table-based layout
 * (compatível com clientes de email tipo Gmail / Outlook).
 */
export function buildEspelhoEmailHTML({
  solicitacao, empresa, tomador, servico,
  // Liga a caixa amarela "atencao - forma de pagamento" acima do disclaimer
  // juridico. Per-replica via config; hoje so BHAZ ativa.
  showAvisoFormaPagamento = false,
}) {
  const theme = ESPELHO_THEMES[empresa?.tema_cor] || ESPELHO_THEMES.azul;
  const ret = calcularRetencoes(solicitacao, servico);
  const linhasCadastradas = ret.linhas.filter(l => l.cadastrada);

  const inscricoes = [
    empresa?.inscricao_estadual && `IE ${empresa.inscricao_estadual}`,
    empresa?.inscricao_municipal && `IM ${empresa.inscricao_municipal}`,
  ].filter(Boolean).join(' · ');

  const logoHtml = empresa?.logo_url
    ? `<img src="${esc(driveImageUrl(empresa.logo_url))}" alt="" width="60" height="60"
         style="width:60px;height:60px;object-fit:contain;background:#f7f7f5;border-radius:8px;padding:6px;border:1px solid #e8e6e1;display:block" />`
    : `<div style="width:60px;height:60px;border-radius:8px;background:${theme.main};color:#fff;font-weight:800;font-size:18px;display:flex;align-items:center;justify-content:center;font-family:Arial,sans-serif">${esc((empresa?.nome_fantasia || 'NF').slice(0, 2).toUpperCase())}</div>`;

  const piHtml = solicitacao.pi
    ? `<div style="font-size:9.5px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;font-weight:600;margin-top:10px">PI</div>
       <div style="font-family:'Courier New',monospace;font-size:12px;font-weight:600">${esc(solicitacao.pi)}</div>`
    : '';

  const inscricoesHtml = inscricoes
    ? `<div style="font-size:11px;color:#6b7280;margin-top:2px">${esc(inscricoes)}</div>` : '';

  const boxHtml = (title, e) => {
    // Entidade do exterior: endereco em linha unica + identificador
    // (em vez do endereco estruturado BR + CNPJ).
    // Nacional: se o documento tem 11 digitos, e Pessoa Fisica — mostra CPF.
    const isExt = !!e?.is_exterior;
    const docDigits = String(e?.cnpj || '').replace(/\D/g, '').length;
    const docLabel = docDigits === 11 ? 'CPF' : 'CNPJ';
    const detailsHtml = isExt
      ? `${e?.endereco_exterior ? `${esc(e.endereco_exterior)}<br />` : ''}` +
        `${e?.cnpj ? `<span style="font-family:'Courier New',monospace;font-size:10.5px">ID ${esc(e.cnpj)}</span>` : ''}`
      : `${e?.logradouro || e?.numero ? `${esc(e?.logradouro)}, ${esc(e?.numero)} — ${esc(e?.bairro)}<br />` : ''}` +
        `${e?.cidade || e?.uf ? `${esc(e?.cidade)}/${esc(e?.uf)} — CEP ${esc(e?.cep)}<br />` : ''}` +
        `<span style="font-family:'Courier New',monospace;font-size:10.5px">${docLabel} ${esc(e?.cnpj || '—')}</span>`;
    return `
    <td valign="top" style="border:1px solid #e8e6e1;border-radius:8px;padding:14px;width:50%">
      <div style="font-size:9.5px;font-weight:700;color:${theme.main};letter-spacing:.08em;text-transform:uppercase;margin-bottom:6px">${esc(title)}</div>
      <div style="font-size:13px;font-weight:700;line-height:1.25">${esc(e?.razao_social || '—')}</div>
      <div style="font-size:11px;color:#3f4548;margin-top:4px;line-height:1.5">
        ${detailsHtml}
      </div>
    </td>`;
  };

  const intermediarioHtml = solicitacao.possui_intermediario ? `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0"
      style="border-collapse:collapse;border:1px solid #e8e6e1;border-radius:8px;margin-bottom:16px">
      <tr><td style="padding:14px">
        <div style="font-size:9.5px;font-weight:700;color:${theme.main};letter-spacing:.08em;text-transform:uppercase;margin-bottom:6px">Intermediário</div>
        <div style="font-size:13px;font-weight:700;line-height:1.25">${esc(solicitacao.intermediario_razao_social || '—')}</div>
        <div style="font-size:11px;color:#3f4548;margin-top:4px">
          <span style="font-family:'Courier New',monospace;font-size:10.5px">CNPJ ${esc(solicitacao.intermediario_cnpj || '—')}</span>
        </div>
      </td></tr>
    </table>` : '';

  const dataRow = (label, value) => `
    <tr>
      <td style="padding:7px 12px;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#6b7280;font-weight:600;width:180px;vertical-align:top;background:#f7f7f5;border-bottom:1px solid #e8e6e1;font-family:Arial,sans-serif">${esc(label)}</td>
      <td style="padding:7px 12px;font-size:11.5px;vertical-align:top;border-bottom:1px solid #e8e6e1;font-family:Arial,sans-serif;white-space:pre-wrap;word-break:break-word">${value || '—'}</td>
    </tr>`;

  const retencoesRows = linhasCadastradas.map(l => dataRow(
    `${l.nome === 'ISS' ? 'ISSQN' : l.nome} · ${fmtPct(l.aliquota)}`,
    `<span style="font-family:'Courier New',monospace">${esc(formatBRL(l.valor))}</span>`
  )).join('');

  const liquidoRow = linhasCadastradas.length > 0
    ? dataRow('Valor líquido', `<strong style="font-family:'Courier New',monospace;color:${theme.dark}">${esc(formatBRL(ret.valorLiquido))}</strong>`)
    : '';

  const greeting = `<p style="font-size:14px;color:#3f4548;margin:0 0 18px 0;font-family:Arial,sans-serif">
    Prezado(a),<br />
    Segue abaixo o espelho da solicitação de nota fiscal para sua validação.
  </p>`;

  return `<!doctype html>
<html lang="pt-BR">
<head><meta charset="utf-8" /><title>Espelho de NF</title></head>
<body style="margin:0;padding:24px;background:#f7f7f5;color:#14171a;font-family:Arial,Helvetica,sans-serif">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:680px;margin:0 auto">
    <tr><td>
      ${greeting}
      <div style="background:#fff;padding:36px;border-radius:8px;border:1px solid #e8e6e1">

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"
          style="padding-bottom:18px;border-bottom:2px solid ${theme.main};margin-bottom:20px">
          <tr>
            <td valign="top" style="padding-right:14px;width:74px">${logoHtml}</td>
            <td valign="top">
              <div style="font-size:9px;font-weight:700;color:${theme.main};letter-spacing:.12em;text-transform:uppercase">
                Espelho · solicitação de NF
              </div>
              <div style="font-size:22px;font-weight:600;margin-top:4px;line-height:1.1;letter-spacing:-0.01em;font-family:Georgia,serif">
                ${esc(empresa?.razao_social || '—')}
              </div>
              ${inscricoesHtml}
            </td>
            <td valign="top" align="right" style="white-space:nowrap">
              <div style="font-size:9.5px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;font-weight:600">ID da solicitação</div>
              <div style="font-family:'Courier New',monospace;font-size:14px;font-weight:600;margin-top:2px">${esc(solicitacao.codigo_unico)}</div>
              ${piHtml}
            </td>
          </tr>
        </table>

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:16px;border-spacing:14px 0">
          <tr>${boxHtml('Prestador', empresa)}${boxHtml('Tomador', tomador)}</tr>
        </table>

        ${intermediarioHtml}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"
          style="background:${theme.soft};border:1px solid ${theme.line};border-radius:8px;margin-bottom:16px">
          <tr>
            <td style="padding:14px 18px">
              <div style="font-size:10px;color:${theme.dark};font-weight:700;letter-spacing:.08em;text-transform:uppercase">Valor bruto</div>
              <div style="font-size:28px;font-weight:700;color:${theme.dark};margin-top:2px;font-family:'Helvetica Neue',Arial,sans-serif">${esc(formatBRL(solicitacao.valor))}</div>
            </td>
            <td align="right" style="padding:14px 18px">
              <div style="font-size:10px;color:${theme.dark};font-weight:700;letter-spacing:.08em;text-transform:uppercase">Competência</div>
              <div style="font-size:14px;font-weight:600;margin-top:2px;text-transform:capitalize">${esc(monthYearLongBR(solicitacao.data_prestacao_servico))}</div>
            </td>
          </tr>
        </table>

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e8e6e1;border-radius:8px;overflow:hidden">
          ${dataRow('Data da solicitação', esc(formatDateBR(solicitacao.data_solicitacao)))}
          ${dataRow('Serviço', esc(servico ? `${servico.codigo} — ${servico.descricao}` : '—'))}
          ${dataRow('Descrição', esc(solicitacao.descricao || '—'))}
          ${retencoesRows}
          ${liquidoRow}
          ${dataRow('Informações complementares', esc(solicitacao.informacoes_complementares || ''))}
          ${dataRow('Previsão de recebimento', esc(solicitacao.previsao_recebimento ? formatDateBR(solicitacao.previsao_recebimento) : '—'))}
          ${dataRow('E-mails para envio', esc(solicitacao.emails_envio || ''))}
        </table>

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:26px">
          <tr>
            <td style="width:50%;padding-right:8px">
              <div style="border-top:1px solid #14171a;padding-top:6px;font-size:11px;color:#6b7280">Solicitante (Comercial)</div>
              <div style="font-size:12px;font-weight:600;margin-top:2px">${esc(solicitacao.solicitante)}</div>
            </td>
            <td style="width:50%;padding-left:8px">
              <div style="border-top:1px solid #14171a;padding-top:6px;font-size:11px;color:#6b7280">Validação e aceite</div>
              <div style="font-size:12px;font-weight:600;margin-top:2px">____________________________</div>
            </td>
          </tr>
        </table>

        <!-- BLOCO DE CONFIRMACAO PELO TOMADOR
             Os placeholders {{APPROVE_URL}} e {{REJECT_URL}} sao
             substituidos no servidor (api/send-email.js) por links
             assinados HMAC. Sem solicitacao_id no payload, os
             placeholders sao removidos e este bloco vira oculto. -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:28px;background:#f4f4f5;border:1px solid #e8e6e1;border-radius:10px">
          <tr><td style="padding:18px 20px">
            <div style="font-size:11px;font-weight:700;color:${theme.main};letter-spacing:.08em;text-transform:uppercase;margin-bottom:8px">
              Confirme o espelho
            </div>
            <div style="font-size:13px;color:#1f2937;line-height:1.5;margin-bottom:14px">
              Revise os dados acima. Se estiver tudo certo, autorize a emissão.
              Caso encontre alguma divergência, recuse e descreva o que precisa
              ser corrigido — vamos ajustar e reenviar.
            </div>
            <!-- Botoes com VML pro Outlook + <a> estilizado pros outros
                 clientes. Padrao consagrado de email-marketing (vide
                 https://buttons.cm/). VML so vai aparecer no Outlook
                 (renderiza nativo) e o <a> abaixo fica mso-hide:all
                 pra nao duplicar la. Mais a prova de balas que ninguem. -->

            <!--[if mso]>
            <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="padding-right:10px">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{APPROVE_URL}}" style="height:44px;v-text-anchor:middle;width:240px;" arcsize="18%" strokecolor="#059669" fillcolor="#059669">
                  <w:anchorlock/>
                  <center style="color:#ffffff;font-family:Arial,sans-serif;font-size:14px;font-weight:bold;">&#10003; Está OK, pode emitir</center>
                </v:roundrect>
              </td>
              <td>
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{REJECT_URL}}" style="height:42px;v-text-anchor:middle;width:240px;" arcsize="20%" strokecolor="#dc2626" fillcolor="#ffffff">
                  <w:anchorlock/>
                  <center style="color:#dc2626;font-family:Arial,sans-serif;font-size:14px;font-weight:bold;">&#10007; Há erros pra corrigir</center>
                </v:roundrect>
              </td>
            </tr></table>
            <![endif]-->

            <!--[if !mso]><!-- -->
            <div style="font-size:0;line-height:0;mso-hide:all">
              <a href="{{APPROVE_URL}}" target="_blank" rel="noopener"
                 style="background-color:#059669;border:1px solid #059669;border-radius:8px;color:#ffffff;display:inline-block;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;line-height:44px;text-align:center;text-decoration:none;width:240px;padding:0;margin:0 10px 8px 0;-webkit-text-size-adjust:none;">
                &#10003; Está OK, pode emitir
              </a>
              <a href="{{REJECT_URL}}" target="_blank" rel="noopener"
                 style="background-color:#ffffff;border:1px solid #dc2626;border-radius:8px;color:#dc2626;display:inline-block;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;line-height:42px;text-align:center;text-decoration:none;width:240px;padding:0;margin:0 0 8px 0;-webkit-text-size-adjust:none;">
                &#10007; Há erros pra corrigir
              </a>
            </div>
            <!--<![endif]-->
            <div style="margin-top:12px;font-size:10.5px;color:#6b7280;line-height:1.5">
              Os links acima são pessoais e identificam você como destinatário deste espelho. Eles abrem uma página segura para registrar sua resposta.
            </div>
          </td></tr>
        </table>

        <div style="margin-top:22px;padding-top:12px;border-top:1px dashed #d6d3cd;font-size:10px;color:#3f4548;text-align:justify;line-height:1.55">
          Quaisquer solicitações de alteração — incluindo correções de valor, escopo, dados cadastrais${showAvisoFormaPagamento ? ', retenções tributárias ou forma de pagamento' : ' ou retenções tributárias'} — deverão ser apresentadas até o
          5º (quinto) dia do mês subsequente ao da competência da nota fiscal. Decorrido esse prazo, <strong>não serão admitidos cancelamentos nem emissões substitutivas</strong>,
          em razão do recolhimento prévio dos tributos incidentes (PIS, COFINS, ISS, IRRF, CSLL e demais retenções devidas) e do encerramento da apuração fiscal do período.
          Eventuais divergências identificadas após esse prazo deverão ser tratadas comercialmente entre as partes, sem reflexo direto na nota já escriturada.${showAvisoFormaPagamento ? ` <strong>Atenção:</strong> a forma de pagamento descrita neste espelho será refletida na nota fiscal emitida e, uma vez aprovada, <strong>não poderá ser alterada</strong> — recomendamos validar essa informação junto à área financeira antes da aprovação, evitando transtornos operacionais.` : ''}
        </div>

        <div style="margin-top:18px;font-size:11px;color:#14171a;text-align:right;font-weight:500">
          ${esc(empresa?.cidade || '—')}, ${esc(formatDateLongBR(solicitacao.data_solicitacao))}.
        </div>
      </div>
    </td></tr>
  </table>
</body>
</html>`;
}

export function buildEspelhoEmailSubject({ solicitacao, empresa, tomador, subjectPrefix, usePIInSubject }) {
  const empNome = empresa?.nome_fantasia || empresa?.razao_social || '—';
  const tomNome = tomador?.nome_fantasia || tomador?.razao_social || '—';
  const prefix = (subjectPrefix && String(subjectPrefix).trim()) || 'Solicitação de NF';
  // Per-replica (BHAZ): em vez do codigo interno OS-XXXXXXXX no final,
  // usa a PI da solicitacao. Fallback pro codigo se a PI estiver vazia.
  const identificador = usePIInSubject && solicitacao.pi
    ? `PI ${solicitacao.pi}`
    : solicitacao.codigo_unico;
  return `${prefix} · ${empNome} → ${tomNome} · ${identificador}`;
}

/* =============================================================================
   Email da NF EMITIDA (confirmacao apos anexar a nota fiscal)
   ============================================================================= */

export function buildNFEmitidaEmailHTML({ solicitacao, empresa, tomador, servico, showReceiptCTAOnTop = false }) {
  const theme = ESPELHO_THEMES[empresa?.tema_cor] || ESPELHO_THEMES.azul;
  const ret = calcularRetencoes(solicitacao, servico);
  const linhasCadastradas = ret.linhas.filter(l => l.cadastrada);
  const anexoUrl = solicitacao.anexo_nota_url || '';
  // Anexos complementares (BHAZ):
  //   - Layout NOVO: array documentos_auxiliares = [{ url, filename }, ...]
  //     Renderiza como lista de links pequenos com o nome real do arquivo.
  //   - Layout ANTIGO (retrocompat): Relatorios (comp_1) e Cartas (comp_2)
  //     como 2 botoes fixos.
  //   - "Documento auxiliar" (anexo_intermediario_url) NUNCA vai — uso interno.
  const docsAuxiliares = Array.isArray(solicitacao.documentos_auxiliares)
    ? solicitacao.documentos_auxiliares.filter(d => d && d.url)
    : [];
  const anexoRelatoriosUrl = solicitacao.anexo_complementar_1_url || '';
  const anexoCartasUrl     = solicitacao.anexo_complementar_2_url || '';

  const inscricoes = [
    empresa?.inscricao_estadual && `IE ${empresa.inscricao_estadual}`,
    empresa?.inscricao_municipal && `IM ${empresa.inscricao_municipal}`,
  ].filter(Boolean).join(' · ');

  const logoHtml = empresa?.logo_url
    ? `<img src="${esc(driveImageUrl(empresa.logo_url))}" alt="" width="60" height="60"
         style="width:60px;height:60px;object-fit:contain;background:#f7f7f5;border-radius:8px;padding:6px;border:1px solid #e8e6e1;display:block" />`
    : `<div style="width:60px;height:60px;border-radius:8px;background:${theme.main};color:#fff;font-weight:800;font-size:18px;display:flex;align-items:center;justify-content:center;font-family:Arial,sans-serif">${esc((empresa?.nome_fantasia || 'NF').slice(0, 2).toUpperCase())}</div>`;

  const inscricoesHtml = inscricoes
    ? `<div style="font-size:11px;color:#6b7280;margin-top:2px">${esc(inscricoes)}</div>` : '';

  const dataRow = (label, value) => `
    <tr>
      <td style="padding:7px 12px;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#6b7280;font-weight:600;width:180px;vertical-align:top;background:#f7f7f5;border-bottom:1px solid #e8e6e1;font-family:Arial,sans-serif">${esc(label)}</td>
      <td style="padding:7px 12px;font-size:11.5px;vertical-align:top;border-bottom:1px solid #e8e6e1;font-family:Arial,sans-serif;white-space:pre-wrap;word-break:break-word">${value || '—'}</td>
    </tr>`;

  const retencoesRows = linhasCadastradas.map(l => dataRow(
    `${l.nome === 'ISS' ? 'ISSQN' : l.nome} · ${fmtPct(l.aliquota)}`,
    `<span style="font-family:'Courier New',monospace">${esc(formatBRL(l.valor))}</span>`
  )).join('');

  const liquidoRow = linhasCadastradas.length > 0
    ? dataRow('Valor líquido', `<strong style="font-family:'Courier New',monospace;color:${theme.dark}">${esc(formatBRL(ret.valorLiquido))}</strong>`)
    : '';

  // Botao secundario (Relatorios / Cartas) — mesmo visual mas outline
  // pra dar hierarquia (nota fiscal em destaque, complementares abaixo).
  const btnSecundario = (url, label, emoji) => url ? `
    <a href="${esc(url)}"
       style="display:inline-block;background:transparent;color:${theme.main};padding:12px 22px;
              border:2px solid ${theme.main};border-radius:12px;text-decoration:none;
              font-weight:700;font-size:13px;font-family:Arial,sans-serif;letter-spacing:.02em;
              margin:4px 6px">
      ${emoji} ${esc(label)}
    </a>` : '';

  // Bloco novo — lista de documentos auxiliares (BHAZ layout multi).
  // Renderiza uma lista clean com o nome real do arquivo em cada link.
  const docsAuxiliaresHtml = docsAuxiliares.length > 0 ? `
    <div style="margin-top:20px;padding:14px 16px;background:#f7f7f5;border:1px solid #e8e6e1;border-radius:8px;font-family:Arial,sans-serif">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;font-weight:600;margin-bottom:10px">
        Documentos auxiliares
      </div>
      ${docsAuxiliares.map(d => `
        <div style="padding:6px 0;font-size:13px">
          <a href="${esc(d.url)}"
             style="color:${theme.main};text-decoration:none;font-weight:500;display:inline-flex;align-items:center;gap:6px"
             target="_blank" rel="noreferrer">
            📎 ${esc(d.filename || 'arquivo.pdf')}
          </a>
        </div>
      `).join('')}
    </div>` : '';

  // Bloco antigo (retrocompat): botoes fixos de Relatorios / Cartas.
  const complementaresHtml = (anexoRelatoriosUrl || anexoCartasUrl) ? `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:8px">
      <tr><td align="center">
        ${btnSecundario(anexoRelatoriosUrl, 'Abrir relatórios', '📊')}
        ${btnSecundario(anexoCartasUrl,     'Abrir cartas',     '✉️')}
      </td></tr>
    </table>` : '';

  // Bloco de confirmacao de recebimento LOGO ABAIXO do 'Abrir arquivo'.
  // Bulletproof button pattern (VML pro Outlook + <a> estilizado pro
  // resto). O placeholder {{RECEIPT_URL}} eh substituido no server
  // (api/send-email.js) por link assinado HMAC.
  const receiptCTATop = showReceiptCTAOnTop ? `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:12px 0 4px">
      <tr><td align="center" style="padding:0 0 10px">
        <div style="font-size:12.5px;color:#3f4548;font-family:Arial,sans-serif;line-height:1.5">
          Sua nota fiscal já foi enviada! 📄 Após conferir, clique abaixo para
          <strong>confirmar o recebimento</strong>. É rapidinho e sua confirmação
          nos ajuda a garantir que está tudo certo com o envio. ✅
        </div>
      </td></tr>
      <tr><td align="center">
        <!--[if mso]>
        <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{RECEIPT_URL}}" style="height:46px;v-text-anchor:middle;width:280px;" arcsize="18%" strokecolor="#059669" fillcolor="#059669">
          <w:anchorlock/>
          <center style="color:#ffffff;font-family:Arial,sans-serif;font-size:14px;font-weight:bold;">&#9993; Confirmo o recebimento da NF</center>
        </v:roundrect>
        <![endif]-->
        <!--[if !mso]><!-- -->
        <div style="font-size:0;line-height:0;mso-hide:all">
          <a href="{{RECEIPT_URL}}" target="_blank" rel="noopener"
             style="background-color:#059669;border:1px solid #059669;border-radius:10px;color:#ffffff;display:inline-block;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;line-height:46px;text-align:center;text-decoration:none;width:280px;padding:0;-webkit-text-size-adjust:none;">
            &#9993; Confirmo o recebimento da NF
          </a>
        </div>
        <!--<![endif]-->
      </td></tr>
      <tr><td align="center" style="padding-top:8px">
        <div style="font-size:10.5px;color:#888;font-family:Arial,sans-serif">
          Link pessoal — registra você como destinatário desta nota.
        </div>
      </td></tr>
    </table>` : '';

  const ctaHtml = anexoUrl ? `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:20px 0 8px">
      <tr><td align="center">
        <a href="${esc(anexoUrl)}"
           style="display:inline-block;background:${theme.main};color:#fff;padding:16px 32px;
                  border-radius:14px;text-decoration:none;font-weight:700;font-size:14px;
                  font-family:Arial,sans-serif;letter-spacing:.02em">
          📄 Abrir arquivo da nota fiscal
        </a>
      </td></tr>
      <tr><td align="center" style="padding-top:8px">
        <div style="font-size:10.5px;color:#888;font-family:Arial,sans-serif">
          Link válido por 1 ano · clique pra baixar ou visualizar
        </div>
      </td></tr>
    </table>
    ${receiptCTATop}
    ${complementaresHtml}
    ${docsAuxiliaresHtml}` : `
    <div style="margin:20px 0;padding:14px 16px;background:#fef3f2;border:1px solid #fda29b;border-radius:8px;color:#b42318;font-size:12px;font-family:Arial,sans-serif;text-align:center">
      ⚠ Arquivo da nota fiscal não foi anexado a este envio. Solicite ao remetente.
    </div>
    ${receiptCTATop}
    ${complementaresHtml}
    ${docsAuxiliaresHtml}`;

  return `<!doctype html>
<html lang="pt-BR">
<head><meta charset="utf-8" /><title>NF emitida</title></head>
<body style="margin:0;padding:24px;background:#f7f7f5;color:#14171a;font-family:Arial,Helvetica,sans-serif">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:680px;margin:0 auto">
    <tr><td>
      <p style="font-size:14px;color:#3f4548;margin:0 0 18px 0;font-family:Arial,sans-serif;line-height:1.55">
        Prezado(a),<br />
        Conforme espelho previamente aprovado entre as partes,
        <strong>a nota fiscal de serviços foi emitida</strong>.
        O arquivo está disponível através do botão abaixo.
      </p>

      <div style="background:#fff;padding:36px;border-radius:8px;border:1px solid #e8e6e1">

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"
          style="padding-bottom:18px;border-bottom:2px solid ${theme.main};margin-bottom:20px">
          <tr>
            <td valign="top" style="padding-right:14px;width:74px">${logoHtml}</td>
            <td valign="top">
              <div style="font-size:9px;font-weight:700;color:${theme.main};letter-spacing:.12em;text-transform:uppercase">
                Nota Fiscal · Emitida
              </div>
              <div style="font-size:22px;font-weight:600;margin-top:4px;line-height:1.1;letter-spacing:-0.01em;font-family:Georgia,serif">
                ${esc(empresa?.razao_social || '—')}
              </div>
              ${inscricoesHtml}
            </td>
            <td valign="top" align="right" style="white-space:nowrap">
              ${solicitacao.numero_nota ? `
                <div style="font-size:9.5px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;font-weight:600">Nº da nota</div>
                <div style="font-family:'Courier New',monospace;font-size:18px;font-weight:700;margin-top:2px;color:${theme.dark}">${esc(solicitacao.numero_nota)}</div>
              ` : ''}
              <div style="font-size:9.5px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;font-weight:600;margin-top:${solicitacao.numero_nota ? 10 : 0}px">Solicitação</div>
              <div style="font-family:'Courier New',monospace;font-size:12px;font-weight:600;margin-top:2px">${esc(solicitacao.codigo_unico)}</div>
            </td>
          </tr>
        </table>

        ${ctaHtml}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"
          style="background:${theme.soft};border:1px solid ${theme.line};border-radius:8px;margin-bottom:16px">
          <tr>
            <td style="padding:14px 18px">
              <div style="font-size:10px;color:${theme.dark};font-weight:700;letter-spacing:.08em;text-transform:uppercase">Valor bruto</div>
              <div style="font-size:24px;font-weight:700;color:${theme.dark};margin-top:2px;font-family:'Helvetica Neue',Arial,sans-serif">${esc(formatBRL(solicitacao.valor))}</div>
            </td>
            <td align="right" style="padding:14px 18px">
              <div style="font-size:10px;color:${theme.dark};font-weight:700;letter-spacing:.08em;text-transform:uppercase">Competência</div>
              <div style="font-size:13px;font-weight:600;margin-top:2px;text-transform:capitalize">${esc(monthYearLongBR(solicitacao.data_prestacao_servico))}</div>
            </td>
          </tr>
        </table>

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e8e6e1;border-radius:8px;overflow:hidden">
          ${dataRow('Tomador', esc(tomador?.razao_social || '—'))}
          ${dataRow('Serviço', esc(servico ? `${servico.codigo} — ${servico.descricao}` : '—'))}
          ${dataRow('Descrição', esc(solicitacao.descricao || '—'))}
          ${retencoesRows}
          ${liquidoRow}
        </table>

        <!-- BOTAO DE CONFIRMACAO DE RECEBIMENTO (card do rodape)
             Placeholder {{RECEIPT_URL}} substituido no servidor
             (api/send-email.js) por link assinado HMAC. Mesmo padrao
             dos botoes do espelho — bulletproof button com VML pro
             Outlook + <a> estilizado pros demais clientes.
             Quando showReceiptCTAOnTop=true (BHAZ), esse card fica
             ESCONDIDO — o botao ja aparece no topo, junto ao 'Abrir
             arquivo', evitando duplicacao. -->
        ${showReceiptCTAOnTop ? '' : `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:24px;background:#f4f4f5;border:1px solid #e8e6e1;border-radius:10px">
          <tr><td style="padding:18px 20px;text-align:center">
            <div style="font-size:11px;font-weight:700;color:${theme.main};letter-spacing:.08em;text-transform:uppercase;margin-bottom:8px">
              Recebeu a nota?
            </div>
            <div style="font-size:13px;color:#1f2937;line-height:1.5;margin-bottom:14px">
              Confirme abaixo que recebeu a nota fiscal e que está tudo certo.
              Isso ajuda nosso financeiro a fechar o ciclo do faturamento.
            </div>

            <!--[if mso]>
            <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{RECEIPT_URL}}" style="height:44px;v-text-anchor:middle;width:280px;" arcsize="18%" strokecolor="#059669" fillcolor="#059669">
              <w:anchorlock/>
              <center style="color:#ffffff;font-family:Arial,sans-serif;font-size:14px;font-weight:bold;">&#9993; Confirmo o recebimento da NF</center>
            </v:roundrect>
            <![endif]-->

            <!--[if !mso]><!-- -->
            <div style="font-size:0;line-height:0;mso-hide:all">
              <a href="{{RECEIPT_URL}}" target="_blank" rel="noopener"
                 style="background-color:#059669;border:1px solid #059669;border-radius:8px;color:#ffffff;display:inline-block;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;line-height:44px;text-align:center;text-decoration:none;width:280px;padding:0;margin:0;-webkit-text-size-adjust:none;">
                &#9993; Confirmo o recebimento da NF
              </a>
            </div>
            <!--<![endif]-->

            <div style="margin-top:12px;font-size:10.5px;color:#6b7280;line-height:1.5">
              Link pessoal — registra você como destinatário desta nota.
            </div>
          </td></tr>
        </table>
        `}

        <div style="margin-top:22px;padding-top:12px;border-top:1px dashed #d6d3cd;font-size:10px;color:#3f4548;text-align:justify;line-height:1.55">
          Em caso de divergência entre os dados informados nesta nota e os contratualmente acordados,
          favor entrar em contato em até 5º (quinto) dia do mês subsequente ao da competência.
          Após esse prazo, em razão do recolhimento prévio dos tributos incidentes, eventuais
          ajustes deverão ser tratados comercialmente entre as partes.
        </div>

        <div style="margin-top:18px;font-size:11px;color:#14171a;text-align:right;font-weight:500">
          ${esc(empresa?.cidade || '—')}, ${esc(formatDateLongBR(solicitacao.data_solicitacao))}.
        </div>
      </div>

      <p style="font-size:11px;color:#888;margin:18px 0 0 0;text-align:center;font-family:Arial,sans-serif">
        Este e-mail é o registro automático da emissão. Para suporte, basta responder esta mensagem.
      </p>
    </td></tr>
  </table>
</body>
</html>`;
}

export function buildNFEmitidaEmailSubject({ solicitacao, empresa, tomador, usePIInSubject }) {
  const empNome = empresa?.nome_fantasia || empresa?.razao_social || '—';
  const tomNome = tomador?.nome_fantasia || tomador?.razao_social || '—';
  const numNota = solicitacao.numero_nota ? `NF ${solicitacao.numero_nota}` : solicitacao.codigo_unico;
  // Per-replica (BHAZ): concatena PI ao final quando existir.
  // Ex: 'Nota fiscal emitida · BRAZ → ACME · NF 1989 · PI 0993930'
  const piSuffix = usePIInSubject && solicitacao.pi ? ` · PI ${solicitacao.pi}` : '';
  return `Nota fiscal emitida · ${empNome} → ${tomNome} · ${numNota}${piSuffix}`;
}
